/*
 * Validatie van het aanvraagformulier in de browser (FR-07).
 *
 * DIT IS EEN DIENST AAN DE BEZOEKER, GEEN CONTROLE. De controle die telt staat in
 * `AanvraagValidator.cs` en draait op de server; wat hier gebeurt is er alleen om
 * een typefout te melden zonder dat er eerst een verzoek de deur uit hoeft.
 *
 * De regels en de meldingen zijn met opzet letterlijk gelijk aan die van de
 * server. Zou de browser strenger zijn, dan blokkeert hij invoer die de server
 * prima vindt; zou hij milder zijn, dan krijgt de bezoeker dezelfde fout twee keer
 * te zien in twee formuleringen. `aanvraagvalidatie.test.ts` legt de meldingen
 * vast, zodat een wijziging aan één kant opvalt.
 *
 * Twee dingen zijn hier niet te doen en horen dus ook niet gemist te worden: de
 * honeypot en de Turnstile-controle. Beide zijn alleen server-side zinvol.
 */
import { keurEmailadres, maximaleVeldlengte } from './veldregels'

/** De velden die het formulier instuurt (FR-05). */
export interface AanvraagFormulierWaarden {
  kaarttype: string
  aanvraagtype: string
  achternaam: string
  tussenvoegsel: string
  voorletters: string
  voornaam: string
  geboortedatum: string
  geslacht: string
  telefoon: string
  straat: string
  huisnummer: string
  postcode: string
  woonplaats: string
  gemeente: string
  email: string
}

/** Veldnaam → melding. Alleen afgekeurde velden staan erin. */
export type Veldfouten = Partial<Record<keyof AanvraagFormulierWaarden, string>>

/**
 * De volgorde waarin de velden op het scherm staan.
 *
 * Hij bepaalt welk veld de focus krijgt na een afgekeurde inzending: het eerste
 * dat fout is, van boven naar beneden. Een fout onderaan waar de focus als eerste
 * heen springt laat de bezoeker de rest missen.
 */
export const veldvolgorde: readonly (keyof AanvraagFormulierWaarden)[] = [
  'kaarttype',
  'aanvraagtype',
  'achternaam',
  'tussenvoegsel',
  'voorletters',
  'voornaam',
  'geboortedatum',
  'geslacht',
  'telefoon',
  'straat',
  'huisnummer',
  'postcode',
  'woonplaats',
  'gemeente',
  'email',
]

/** Ouder dan dit bestaat niet; een typefout in het jaartal wel. */
const maximaleLeeftijd = 120

const kaarttypen = ['B', 'P', 'B+P']
const aanvraagtypen = ['eerste', 'verlenging']
const geslachten = ['man', 'vrouw', 'anders']

const postcodePatroon = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/

const verplicht: Partial<Record<keyof AanvraagFormulierWaarden, string>> = {
  achternaam: 'Vul uw achternaam in.',
  voorletters: 'Vul uw voorletters in.',
  voornaam: 'Vul uw eerste voornaam voluit in.',
  straat: 'Vul uw straatnaam in.',
  huisnummer: 'Vul uw huisnummer in.',
  woonplaats: 'Vul uw woonplaats in.',
  gemeente: 'Vul uw gemeente in.',
}

const teLang = `Dit veld mag hoogstens ${maximaleVeldlengte} tekens bevatten.`

/**
 * Keurt alle velden en levert de meldingen per veld.
 *
 * @param nu Vandaag, om de geboortedatum tegen af te zetten. Meegegeven in plaats
 * van hier opgehaald, zodat een test niet van de systeemklok afhangt.
 */
export function valideerAanvraag(
  waarden: AanvraagFormulierWaarden,
  nu: Date = new Date(),
): Veldfouten {
  const fouten: Veldfouten = {}

  for (const [veld, melding] of Object.entries(verplicht) as [
    keyof AanvraagFormulierWaarden,
    string,
  ][]) {
    if (waarden[veld].trim() === '') {
      fouten[veld] = melding
    } else if (waarden[veld].length > maximaleVeldlengte) {
      fouten[veld] = teLang
    }
  }

  if (waarden.tussenvoegsel.length > maximaleVeldlengte) {
    fouten.tussenvoegsel = teLang
  }

  if (!kaarttypen.includes(waarden.kaarttype)) {
    fouten.kaarttype = 'Kies welke kaart u aanvraagt.'
  }

  if (!aanvraagtypen.includes(waarden.aanvraagtype)) {
    fouten.aanvraagtype =
      'Kies of dit een eerste aanvraag of een verlenging is.'
  }

  if (!geslachten.includes(waarden.geslacht)) {
    fouten.geslacht = 'Kies uw geslacht.'
  }

  const geboortedatum = keurGeboortedatum(waarden.geboortedatum, nu)
  if (geboortedatum) fouten.geboortedatum = geboortedatum

  const postcode = keurPostcode(waarden.postcode)
  if (postcode) fouten.postcode = postcode

  const telefoon = keurTelefoon(waarden.telefoon)
  if (telefoon) fouten.telefoon = telefoon

  const email = keurEmailadres(waarden.email)
  if (email) fouten.email = email

  return fouten
}

function keurGeboortedatum(waarde: string, nu: Date): string | undefined {
  if (waarde.trim() === '') return 'Vul uw geboortedatum in.'

  // Alleen dd-mm-jjjj, net als de server: een tweede notatie toestaan maakt
  // 03-05 en 05-03 stilzwijgend verwisselbaar.
  const delen = /^(\d{2})-(\d{2})-(\d{4})$/.exec(waarde)
  if (!delen) {
    return 'Vul uw geboortedatum in als dag-maand-jaar, bijvoorbeeld 03-05-1952.'
  }

  const dag = Number(delen[1])
  const maand = Number(delen[2])
  const jaar = Number(delen[3])

  // Via setFullYear en niet via de constructor: die maakt van jaartal 50 het jaar
  // 1950, en dan zou 03-05-0050 hier een andere melding krijgen dan op de server.
  const datum = new Date(0, 0, 1)
  datum.setFullYear(jaar, maand - 1, dag)

  // JavaScript rolt 31-02 door naar 3 maart in plaats van het af te keuren; de
  // enige manier om dat te merken is terugkijken wat er van de invoer over is.
  if (
    datum.getFullYear() !== jaar ||
    datum.getMonth() !== maand - 1 ||
    datum.getDate() !== dag
  ) {
    return 'Vul uw geboortedatum in als dag-maand-jaar, bijvoorbeeld 03-05-1952.'
  }

  const vandaag = new Date(nu.getFullYear(), nu.getMonth(), nu.getDate())
  if (datum > vandaag) return 'Deze datum ligt in de toekomst.'

  const oudst = new Date(vandaag)
  oudst.setFullYear(oudst.getFullYear() - maximaleLeeftijd)
  if (datum < oudst) return 'Controleer het jaartal van uw geboortedatum.'

  return undefined
}

function keurPostcode(waarde: string): string | undefined {
  if (waarde.trim() === '') return 'Vul uw postcode in.'

  return postcodePatroon.test(waarde.trim())
    ? undefined
    : 'Vul uw postcode in als 1234 AB.'
}

function keurTelefoon(waarde: string): string | undefined {
  if (waarde.trim() === '') return 'Vul uw telefoonnummer in.'

  // Bewust ruim, gelijk aan de server: spaties, streepjes, haakjes en +31 komen
  // allemaal voor, en een te streng patroon weigert geldige nummers.
  const cijfers = [...waarde].filter((teken) => teken >= '0' && teken <= '9')
  const onbekendTeken = [...waarde].some(
    (teken) => !(teken >= '0' && teken <= '9') && !' -()+'.includes(teken),
  )

  return cijfers.length >= 10 && cijfers.length <= 13 && !onbekendTeken
    ? undefined
    : 'Vul een geldig telefoonnummer in, bijvoorbeeld 0612345678.'
}
